// Phase 0093-B1 — Study Direction Engine API client.
import apiClient from '@/shared/api/client'

export interface DirectionRule {
  id: string
  study_id: string
  study_code: string
  rule_code: string
  rule_name: string
  trigger_type: string
  frequency: string | null
  department_scope: string | null
  shift_scope: string | null
  machine_scope: string | null
  employee_scope: string | null
  role_scope: string | null
  selection_logic: string
  sample_size: number | null
  threshold_config_json: Record<string, unknown> | null
  priority: string
  due_time_local: string | null
  owner_role_code: string
  verifier_role_code: string | null
  active: boolean
  is_default_rule: boolean
  system_seeded: boolean
  factory_version: number
  admin_modified_at: string | null
  last_seeded_at: string | null
  remarks: string | null
}

export interface DirectionToday {
  id: string
  title: string
  study_id: string | null
  study_template_id: string | null
  target_department: string | null
  target_department_id: string | null
  target_employee: string | null
  target_employee_id: string | null
  target_machine_id: string | null
  target_reason: string | null
  target_priority: string | null
  suggested_action: string | null
  due_at: string | null
  status: string
  source_metric: string | null
  source_metric_value: string | null
  rule_id: string | null
}

export interface GenerationLog {
  id: string
  rule_code: string
  run_date: string
  run_time: string
  trigger_type: string
  source_records_found: number
  tasks_created: number
  tasks_skipped_duplicate: number
  status: string
  error_message: string | null
}

const P = '/hr/study-directions'

export const studyDirectionApi = {
  rules: async (params: { active_only?: boolean; trigger_type?: string } = {}) =>
    (await apiClient.get<DirectionRule[]>(`${P}/rules`, { params })).data,
  updateRule: async (id: string, payload: Partial<DirectionRule>) =>
    (await apiClient.patch<DirectionRule>(`${P}/rules/${id}`, payload)).data,
  deactivate: async (id: string) =>
    (await apiClient.post<DirectionRule>(`${P}/rules/${id}/deactivate`)).data,
  duplicate: async (id: string, new_code: string) =>
    (await apiClient.post<DirectionRule>(
      `${P}/rules/${id}/duplicate`, { new_code })).data,
  resetToFactory: async (id: string) =>
    (await apiClient.post<DirectionRule>(
      `${P}/rules/${id}/reset-to-factory`)).data,
  runNow: async (id: string, on_date?: string) =>
    (await apiClient.post<GenerationLog>(
      `${P}/rules/${id}/run-now`, undefined,
      { params: on_date ? { on_date } : {} })).data,
  runAll: async (on_date?: string) =>
    (await apiClient.post<Array<{ rule_code: string; tasks_created: number; tasks_skipped_duplicate: number; status: string }>>(
      `${P}/run-all`, undefined,
      { params: on_date ? { on_date } : {} })).data,
  reseed: async () =>
    (await apiClient.post<Record<string, number>>(`${P}/reseed`)).data,
  today: async (me = false, on_date?: string) =>
    (await apiClient.get<DirectionToday[]>(`${P}/today`,
      { params: { me, on_date } })).data,
  logs: async (rule_id?: string, limit = 50) =>
    (await apiClient.get<GenerationLog[]>(`${P}/logs`,
      { params: { rule_id, limit } })).data,
}
